import { z } from 'zod';

import {
  BRAND_MOCKUP_DEFAULT_ITEMS,
  BRAND_MOCKUP_ITEMS,
  BRAND_MOCKUP_MAX_ITEMS,
  BRAND_MOCKUP_STYLES,
} from './brand-mockups.js';

/**
 * ═══════════════════════════════════════════════════════════════════════════
 * HIZLI AKIŞLAR — sohbetin yapılandırılmış giriş kapısı (4 Eki 2026)
 * ═══════════════════════════════════════════════════════════════════════════
 *
 * Kullanıcı isteği: *"chatgpt'deki /brandasset gibi… görsel olacak adam
 * tıklayacak ne istediğini seçecek, logo atın diyecek, AI otomatik yapacak;
 * kampanyaya tıklayacak bir şey soracak… chat inanılmaz gelişmiş ve
 * ergonomik olmalı"*.
 *
 * Bir akış = yazma kutusunun İÇİNDE açılan kısa bir form. Seçimler (tür,
 * boyut, adet), yükleme (logo/ürün fotoğrafı) ve katalog seçimi YERELDE ve
 * ANINDA yapılıyor — bir dosya seçtirmek ya da dört şıktan birini
 * işaretletmek bir LLM turuna (10–15 sn + günlük hak) değmez; kreatif
 * direktörü aynı kararı vermişti (`creative-skills.ts` → `needsProduct`).
 * Gönderince cevaplar mesajın YANINDA yapılandırılmış bir blok olarak gidiyor
 * (`mentorivo_flow`) ve model işe doğrudan giriyor: sorulanı tekrar sormuyor,
 * yalnız akışın bilerek boş bıraktığı yerde (bütçe yoksa, eksik kimlik
 * varsa) soruyor.
 *
 * ---------------------------------------------------------------------------
 * 🔴 Niyet düğmelerinin (AGENT_INTENTS) yerini aldı — sözleşmeden silinmedi
 * ---------------------------------------------------------------------------
 * Eski dört niyet yalnız "bu mesaj kampanyayla ilgili" diyen bir ipucuydu.
 * Akış onun üst kümesi: hangi iş, hangi girdilerle. Eski mesajlardaki
 * `mentorivo_intent` blokları okunmaya devam ediyor (`intentHint`).
 *
 * Metne önek gömmek (`/mockup …`) hâlâ REDDEDİLMİŞ karar (`AGENT_INTENTS`
 * başlığı): `/` yazmak yalnız PALETİ açıyor, seçim metinden silinip akışa
 * dönüşüyor. Gönderilen metin kullanıcının gördüğü metin.
 *
 * ---------------------------------------------------------------------------
 * Bu dosyada ne var, ne yok
 * ---------------------------------------------------------------------------
 *   · VAR — akışların veri tanımı (adımlar, seçenek kümeleri, varsayılanlar),
 *     mesaj şeması, TEK doğrulama fonksiyonu (`checkAgentFlow`): arayüz
 *     "gönder" düğmesini, API mesajı aynı fonksiyonla kapatıyor.
 *   · YOK — modele giden reçeteler: `@mentorivo/shared/agent-flow-recipes`
 *     (alt yol, barrel'da değil) — sahne reçeteleriyle aynı karar.
 *   · YOK — görünen metin: `dashboard.agentChat.flows.*` (13 dil).
 */

export const AGENT_FLOW_BLOCK_TYPE = 'mentorivo_flow';

export const AGENT_FLOW_GROUPS = [
  'image',
  'video',
  'brand',
  'campaign',
  'analysis',
  'catalog',
] as const;
export type AgentFlowGroup = (typeof AGENT_FLOW_GROUPS)[number];

/**
 * Yazma kutusunun alt satırında DÜĞME olarak duran gruplar. Öteki ikisi
 * (analiz, katalog) "Tümü" paletinde ve `/` ile. Dört düğme + "Tümü" mobilde
 * tek satıra sığan en fazla şey (5 × 44px dokunmatik hedef).
 *
 * 5 Eki 2026 — "Otomasyon" grubu (kural kur, kurallarımı gözden geçir)
 * kalktı, kullanıcı kararı: kural kurmak artık ne kullanıcının ne AI'ın işi;
 * AI operatör devriyesiyle izliyor.
 */
export const AGENT_FLOW_PINNED_GROUPS: readonly AgentFlowGroup[] = ['image', 'video', 'brand', 'campaign'];

/** Kampanya akışında seçilebilen reklam platformları — `AdPlatform`ın reklam verilebilen beşi. */
export const AGENT_FLOW_PLATFORMS = ['META', 'GOOGLE', 'TIKTOK', 'LINKEDIN', 'TWITTER'] as const;

/**
 * Bir mesajda en fazla kaç ek — `AGENT_ATTACHMENT_LIMITS.maxPerMessage` ile
 * AYNI. Buraya import edilmedi: `agent.ts` bu dosyayı import ediyor (mesaj
 * şeması) ve ters yön değerlerde döngü doğurur. Eşitliği API spec'i denetliyor.
 */
export const AGENT_FLOW_MAX_ATTACHMENTS = 4;

// ═══════════════════════════════════════════════════════════════════════════
// Seçenek kümeleri — etiketler `flows.set.<küme>.<seçenek>`
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Kimlikler çeviri ANAHTARI olarak da kullanılıyor, o yüzden nokta ve iki
 * nokta YOK (`9:16` değil `vertical`). Gerçek değere çeviri reçetede.
 */
export const AGENT_FLOW_SETS = {
  imageFormat: ['feed_square', 'feed_portrait', 'story', 'landscape'],
  videoAspect: ['vertical', 'portrait', 'square', 'wide'],
  count: ['1', '2', '3', '4'],
  imageMood: ['clean', 'natural', 'luxury', 'bold'],
  setting: ['home', 'kitchen', 'bathroom', 'office', 'outdoor', 'cafe'],
  person: ['woman', 'man'],
  editAction: ['resize', 'upscale', 'variations'],
  setType: ['series', 'carousel', 'banner_set'],
  mockupItem: BRAND_MOCKUP_ITEMS,
  mockupStyle: BRAND_MOCKUP_STYLES,
  voice: ['tr', 'en'],
  goal: ['sales', 'traffic', 'awareness', 'leads'],
  duration: ['d7', 'd14', 'd30', 'ongoing'],
  focus: ['budget', 'creative', 'audience', 'bidding', 'schedule'],
  scope: ['all', 'top', 'losing'],
  splitTarget: ['roas', 'cpa', 'conversions'],
  period: ['yesterday', 'd7', 'd30', 'month'],
  compare: ['previous', 'none'],
  metric: ['roas', 'cpa', 'ctr', 'spend', 'conversions', 'cpm'],
} as const satisfies Record<string, readonly string[]>;
export type AgentFlowSet = keyof typeof AGENT_FLOW_SETS;

// ═══════════════════════════════════════════════════════════════════════════
// Adımlar
// ═══════════════════════════════════════════════════════════════════════════

/**
 * Bir varlık adımının kabul ettiği kaynaklar:
 *
 *   upload   kullanıcının dosyası — mesajın eki olarak gidiyor (`{attachment}`)
 *   catalog  bağlı mağazadan ürün (`{ref:'product'}`)
 *   library  kütüphaneden — rol `logo` ise marka logoları, `creative` ise
 *            hazır kreatifler (`{ref:'logo'|'creative'}`)
 *   find     logoyu bağlı reklam hesaplarında bul (`{find:true}`) — model
 *            `brand_logo_from_platform` çağırıyor
 */
export type AgentFlowAssetSource = 'upload' | 'catalog' | 'library' | 'find';
export type AgentFlowAssetRole = 'product' | 'logo' | 'creative';

interface StepBase {
  /** Cevap anahtarı ve etiket anahtarı (`flows.step.<id>`). */
  id: string;
  required: boolean;
  /** Yalnız bu adımın cevabı listedeyken görünür (ve geçerli) — ör. boyutlandırmada formatlar. */
  showIf?: { step: string; in: readonly string[] };
}

export type AgentFlowStep =
  | (StepBase & {
      kind: 'choice';
      set: AgentFlowSet;
      /** Kümenin alt kümesi; yoksa kümenin tamamı. */
      options?: readonly string[];
      default?: string;
    })
  | (StepBase & {
      kind: 'multi';
      set: AgentFlowSet;
      options?: readonly string[];
      min: number;
      max: number;
      default?: readonly string[];
    })
  | (StepBase & { kind: 'asset'; role: AgentFlowAssetRole; sources: readonly AgentFlowAssetSource[] })
  /** Para — çalışma alanının para biriminde (birim reçetede yazılıyor). */
  | (StepBase & { kind: 'amount'; min: number; max: number })
  | (StepBase & { kind: 'text'; maxLength: number })
  /** Bağlı reklam platformlarından seçim — seçenekler ÇALIŞMA ANINDA (bağlantılardan). */
  | (StepBase & { kind: 'platforms'; min: number; max: number });

export const AGENT_FLOW_IDS = [
  // Görsel
  'image-product',
  'image-lifestyle',
  'image-model',
  'image-promo',
  'image-set',
  'image-edit',
  // Video
  'video-ad',
  'video-clip',
  'video-ugc',
  'video-template',
  // Marka
  'brand-mockups',
  'brand-kit',
  'brand-logo-find',
  // Kampanya
  'campaign-new',
  'campaign-optimize',
  'campaign-budget',
  // Analiz
  'analysis-report',
  'analysis-diagnose',
  'analysis-creatives',
  'analysis-competitors',
  // Katalog
  'catalog-health',
  'catalog-fix',
] as const;
export type AgentFlowId = (typeof AGENT_FLOW_IDS)[number];

export interface AgentFlowDef {
  id: AgentFlowId;
  group: AgentFlowGroup;
  steps: readonly AgentFlowStep[];
  /**
   * `/` paletindeki arama — çevrilmiş adın YANINDA. Küçük harf, aksansız;
   * iki dilin de sık kelimeleri ("mockup", "brandasset", "logo", "tisort"):
   * Türkçe panelde "brand" yazan da bulmalı.
   */
  keywords: readonly string[];
  /**
   * Palette küçük resim — ürünün GERÇEK çıktısı (`/templates/*.webp`, sahne
   * şablonlarının kendi reçeteleriyle üretildi; `creative-skills.ts`teki
   * önizlemelerin aynısı). Yoksa ikon.
   */
  preview?: string;
}

/* ── Sık adımlar ─────────────────────────────────────────────────────────── */

const productStep = (required: boolean, sources: readonly AgentFlowAssetSource[] = ['catalog', 'upload']): AgentFlowStep => ({
  id: 'product',
  kind: 'asset',
  role: 'product',
  sources,
  required,
});
const imageFormat = (fallback: string = 'feed_square'): AgentFlowStep => ({
  id: 'format',
  kind: 'choice',
  set: 'imageFormat',
  default: fallback,
  required: true,
});
const count: AgentFlowStep = { id: 'count', kind: 'choice', set: 'count', default: '2', required: true };
const period: AgentFlowStep = { id: 'period', kind: 'choice', set: 'period', default: 'd7', required: true };

const DEFS: Record<AgentFlowId, Omit<AgentFlowDef, 'id'>> = {
  // ── Görsel ──────────────────────────────────────────────────────────────
  'image-product': {
    group: 'image',
    preview: '/templates/pastel-props-studio.webp',
    keywords: ['gorsel', 'image', 'urun', 'product', 'vitrin', 'packshot', 'foto', 'photo'],
    steps: [
      productStep(true),
      { id: 'mood', kind: 'choice', set: 'imageMood', required: false },
      imageFormat(),
      count,
    ],
  },
  'image-lifestyle': {
    group: 'image',
    preview: '/templates/mint-bathroom-counter.webp',
    keywords: ['yasam', 'lifestyle', 'sahne', 'scene', 'ortam', 'mekan'],
    steps: [
      productStep(true),
      { id: 'setting', kind: 'choice', set: 'setting', required: false },
      imageFormat('feed_portrait'),
      count,
    ],
  },
  'image-model': {
    group: 'image',
    preview: '/templates/straw-bag-summer.webp',
    keywords: ['manken', 'model', 'kisi', 'person', 'insan', 'giyim'],
    steps: [
      productStep(true),
      { id: 'person', kind: 'choice', set: 'person', required: false },
      imageFormat('feed_portrait'),
      count,
    ],
  },
  'image-promo': {
    group: 'image',
    preview: '/templates/desert-speed-bar.webp',
    keywords: ['kampanya', 'indirim', 'promo', 'sale', 'discount', 'post', 'firsat'],
    steps: [
      productStep(true),
      { id: 'offer', kind: 'text', maxLength: 140, required: true },
      imageFormat(),
      count,
    ],
  },
  'image-set': {
    group: 'image',
    keywords: ['set', 'seri', 'series', 'karusel', 'carousel', 'banner', 'kampanya seti'],
    steps: [
      productStep(true),
      { id: 'setType', kind: 'choice', set: 'setType', default: 'series', required: true },
      { id: 'aspect', kind: 'choice', set: 'videoAspect', default: 'square', required: true },
    ],
  },
  'image-edit': {
    group: 'image',
    keywords: ['duzenle', 'edit', 'boyut', 'resize', 'buyut', 'upscale', 'varyasyon', 'variation'],
    steps: [
      { id: 'creative', kind: 'asset', role: 'creative', sources: ['library'], required: true },
      { id: 'action', kind: 'choice', set: 'editAction', default: 'resize', required: true },
      {
        id: 'formats',
        kind: 'multi',
        set: 'imageFormat',
        min: 1,
        max: 4,
        default: ['story', 'feed_portrait'],
        required: true,
        showIf: { step: 'action', in: ['resize'] },
      },
    ],
  },

  // ── Video ───────────────────────────────────────────────────────────────
  'video-ad': {
    group: 'video',
    keywords: ['video', 'reklam', 'ad', 'reels', 'tiktok', 'film', 'story'],
    steps: [
      productStep(true),
      { id: 'aspect', kind: 'choice', set: 'videoAspect', default: 'vertical', required: true },
    ],
  },
  'video-clip': {
    group: 'video',
    preview: '/templates/arc-light-tunnel.webp',
    keywords: ['klip', 'clip', 'hizli', 'quick', 'urun videosu', 'product video'],
    steps: [productStep(true), { id: 'mood', kind: 'choice', set: 'imageMood', required: false }],
  },
  'video-ugc': {
    group: 'video',
    preview: '/templates/hand-to-hand.webp',
    keywords: ['ugc', 'sunucu', 'presenter', 'konusan', 'talking', 'influencer', 'yorum'],
    steps: [
      productStep(true),
      { id: 'person', kind: 'choice', set: 'person', required: false },
      { id: 'voice', kind: 'choice', set: 'voice', default: 'tr', required: true },
    ],
  },
  'video-template': {
    group: 'video',
    preview: '/templates/speed-trail-studio.webp',
    keywords: ['sablon', 'template', 'hazir', 'ready'],
    steps: [productStep(true)],
  },

  // ── Marka ───────────────────────────────────────────────────────────────
  'brand-mockups': {
    group: 'brand',
    keywords: ['mockup', 'brandasset', 'brand asset', 'marka', 'brand', 'logo', 'tisort', 'kupa', 'kartvizit'],
    steps: [
      { id: 'logo', kind: 'asset', role: 'logo', sources: ['upload', 'library', 'find'], required: true },
      {
        id: 'items',
        kind: 'multi',
        set: 'mockupItem',
        min: 1,
        max: BRAND_MOCKUP_MAX_ITEMS,
        default: BRAND_MOCKUP_DEFAULT_ITEMS,
        required: true,
      },
      { id: 'style', kind: 'choice', set: 'mockupStyle', default: 'studio', required: true },
      imageFormat(),
    ],
  },
  'brand-kit': {
    group: 'brand',
    keywords: ['marka kiti', 'brand kit', 'renk', 'color', 'font', 'ton', 'tone', 'kimlik', 'identity'],
    steps: [
      { id: 'logo', kind: 'asset', role: 'logo', sources: ['upload', 'library', 'find'], required: false },
      { id: 'website', kind: 'text', maxLength: 200, required: false },
    ],
  },
  'brand-logo-find': {
    group: 'brand',
    keywords: ['logo bul', 'find logo', 'logom', 'my logo', 'profil'],
    steps: [],
  },

  // ── Kampanya ────────────────────────────────────────────────────────────
  'campaign-new': {
    group: 'campaign',
    keywords: ['kampanya', 'campaign', 'yeni', 'new', 'kur', 'launch', 'reklam ver'],
    steps: [
      productStep(false, ['catalog']),
      { id: 'goal', kind: 'choice', set: 'goal', default: 'sales', required: true },
      { id: 'budget', kind: 'amount', min: 1, max: 10_000_000, required: true },
      { id: 'duration', kind: 'choice', set: 'duration', default: 'd14', required: true },
      { id: 'platforms', kind: 'platforms', min: 1, max: AGENT_FLOW_PLATFORMS.length, required: false },
    ],
  },
  'campaign-optimize': {
    group: 'campaign',
    keywords: ['iyilestir', 'optimize', 'optimizasyon', 'performans', 'improve'],
    steps: [
      { id: 'scope', kind: 'choice', set: 'scope', default: 'all', required: true },
      { id: 'focus', kind: 'multi', set: 'focus', min: 1, max: 5, required: false },
    ],
  },
  'campaign-budget': {
    group: 'campaign',
    keywords: ['butce', 'budget', 'dagit', 'split', 'yeniden dagit', 'reallocate'],
    steps: [
      { id: 'budget', kind: 'amount', min: 1, max: 10_000_000, required: false },
      { id: 'target', kind: 'choice', set: 'splitTarget', default: 'roas', required: true },
    ],
  },

  // ── Analiz ──────────────────────────────────────────────────────────────
  'analysis-report': {
    group: 'analysis',
    keywords: ['rapor', 'report', 'ozet', 'summary', 'performans', 'nasil gidiyor'],
    steps: [period, { id: 'compare', kind: 'choice', set: 'compare', default: 'previous', required: true }],
  },
  'analysis-diagnose': {
    group: 'analysis',
    keywords: ['neden', 'why', 'dustu', 'drop', 'teshis', 'diagnose', 'degisim'],
    steps: [{ id: 'metric', kind: 'choice', set: 'metric', default: 'roas', required: true }, period],
  },
  'analysis-creatives': {
    group: 'analysis',
    keywords: ['kreatif performansi', 'creative performance', 'en iyi reklam', 'best ads', 'yorgunluk'],
    steps: [period],
  },
  'analysis-competitors': {
    group: 'analysis',
    keywords: ['rakip', 'competitor', 'rival', 'kutuphane', 'ad library'],
    steps: [{ id: 'competitor', kind: 'text', maxLength: 120, required: true }],
  },

  // ── Katalog ─────────────────────────────────────────────────────────────
  'catalog-health': {
    group: 'catalog',
    keywords: ['katalog', 'catalog', 'saglik', 'health', 'urunler', 'products', 'eksik'],
    steps: [],
  },
  'catalog-fix': {
    group: 'catalog',
    keywords: ['urun metni', 'product copy', 'aciklama', 'description', 'baslik', 'title', 'duzelt', 'fix'],
    steps: [productStep(false, ['catalog'])],
  },
};

export const AGENT_FLOWS: readonly AgentFlowDef[] = AGENT_FLOW_IDS.map((id) => ({ id, ...DEFS[id] }));

export function findAgentFlow(id: string): AgentFlowDef | null {
  return AGENT_FLOWS.find((flow) => flow.id === id) ?? null;
}

/** Adımın seçenekleri — `options` verilmediyse kümenin tamamı. */
export function agentFlowOptions(step: Extract<AgentFlowStep, { set: AgentFlowSet }>): readonly string[] {
  return step.options ?? AGENT_FLOW_SETS[step.set];
}

// ═══════════════════════════════════════════════════════════════════════════
// Mesaj şeması
// ═══════════════════════════════════════════════════════════════════════════

/** Katalog ürünü / kayıtlı logo / hazır kreatif — kimlik + okunur ad (ad VERİ, modele tırnakla gider). */
export const agentFlowRefSchema = z
  .object({
    ref: z.enum(['product', 'logo', 'creative']),
    id: z.uuid(),
    label: z.string().trim().max(200).optional(),
  })
  .strict();

/** Mesajın kaçıncı eki (0'dan) — sunucu kimliğe (`a3`) çeviriyor. */
export const agentFlowAttachmentSchema = z
  .object({ attachment: z.number().int().min(0).max(AGENT_FLOW_MAX_ATTACHMENTS - 1) })
  .strict();

/** "Logoyu bağlı hesaplarımda bul". */
export const agentFlowFindSchema = z.object({ find: z.literal(true) }).strict();

export const agentFlowAnswerSchema = z.union([
  z.string().trim().min(1).max(600),
  z.array(z.string().trim().min(1).max(60)).min(1).max(12),
  z.number().finite().min(0).max(1_000_000_000),
  agentFlowRefSchema,
  agentFlowAttachmentSchema,
  agentFlowFindSchema,
]);
export type AgentFlowAnswer = z.infer<typeof agentFlowAnswerSchema>;
export type AgentFlowAnswers = Record<string, AgentFlowAnswer>;

export const agentFlowRequestSchema = z.object({
  id: z.enum(AGENT_FLOW_IDS),
  answers: z.record(z.string().regex(/^[a-z][a-zA-Z0-9]{0,31}$/), agentFlowAnswerSchema).default({}),
});
export type AgentFlowRequest = z.infer<typeof agentFlowRequestSchema>;

/** Mesajda saklanan blok — `appendUserMessage` yazıyor, arayüz özet çipini buradan basıyor. */
export interface AgentFlowBlock {
  type: typeof AGENT_FLOW_BLOCK_TYPE;
  id: AgentFlowId;
  answers: AgentFlowAnswers;
}

// ═══════════════════════════════════════════════════════════════════════════
// Doğrulama — arayüz ve API AYNI fonksiyon
// ═══════════════════════════════════════════════════════════════════════════

export type AgentFlowProblem = 'unknown_step' | 'missing' | 'invalid';

export type AgentFlowCheck =
  | { ok: true; answers: AgentFlowAnswers }
  | { ok: false; step: string; problem: AgentFlowProblem };

/** Adım şu cevaplarla görünüyor mu (`showIf`). */
export function agentFlowStepVisible(step: AgentFlowStep, answers: AgentFlowAnswers): boolean {
  if (!step.showIf) return true;
  const value = answers[step.showIf.step];
  return typeof value === 'string' && step.showIf.in.includes(value);
}

/**
 * Akışın başlangıç cevapları — varsayılanı olan seçimler dolu gelir.
 * Arayüz formu bununla açıyor; sunucu eksik zorunlu seçimi bununla tamamlıyor.
 */
export function agentFlowDefaults(flow: AgentFlowDef): AgentFlowAnswers {
  const out: AgentFlowAnswers = {};
  for (const step of flow.steps) {
    if (step.kind === 'choice' && step.default) out[step.id] = step.default;
    if (step.kind === 'multi' && step.default) out[step.id] = [...step.default];
  }
  return out;
}

/**
 * Cevapları akışın tanımına göre denetler ve NORMALLEŞTİRİR.
 *
 *   · Tanımda olmayan anahtar → `unknown_step` (sessizce atmak, istemcinin
 *     yanlış bir sözleşmeyle konuştuğunu saklardı).
 *   · Gizli adımın cevabı (`showIf` tutmuyor) → atılır: kullanıcı önce
 *     "boyutlandır" seçip formatları işaretledi, sonra "büyüt"e döndüyse o
 *     formatlar artık bir şey ifade etmiyor.
 *   · Zorunlu seçim boşsa varsayılanı yazılır; varsayılan yoksa `missing`.
 *   · Çoklu seçim tekilleştirilir, kümede olmayan değer `invalid`.
 *   · Ek dizini mesajın ek sayısını aşamaz; `find` yalnız `find` kaynaklı adımda.
 */
export function checkAgentFlow(
  request: { id: string; answers?: AgentFlowAnswers },
  context: { attachments: number },
): AgentFlowCheck {
  const flow = findAgentFlow(request.id);
  if (!flow) return { ok: false, step: 'id', problem: 'invalid' };
  const given = request.answers ?? {};
  for (const key of Object.keys(given)) {
    if (!flow.steps.some((step) => step.id === key)) return { ok: false, step: key, problem: 'unknown_step' };
  }

  const answers: AgentFlowAnswers = {};
  /* Sıra tanımın sırası: `showIf` önceki adımın NORMALLEŞMİŞ cevabına bakıyor. */
  for (const step of flow.steps) {
    if (!agentFlowStepVisible(step, answers)) continue;
    let value: AgentFlowAnswer | undefined = given[step.id];

    if (value === undefined) {
      if (step.kind === 'choice' && step.default) value = step.default;
      else if (step.kind === 'multi' && step.default) value = [...step.default];
      else if (step.required) return { ok: false, step: step.id, problem: 'missing' };
      else continue;
    }

    const normalized = normalizeAnswer(step, value, context);
    if (normalized === null) return { ok: false, step: step.id, problem: 'invalid' };
    answers[step.id] = normalized;
  }
  return { ok: true, answers };
}

function normalizeAnswer(
  step: AgentFlowStep,
  value: AgentFlowAnswer,
  context: { attachments: number },
): AgentFlowAnswer | null {
  switch (step.kind) {
    case 'choice': {
      if (typeof value !== 'string') return null;
      return agentFlowOptions(step).includes(value) ? value : null;
    }
    case 'multi': {
      if (!Array.isArray(value)) return null;
      const unique = [...new Set(value)];
      const options = agentFlowOptions(step);
      if (unique.some((item) => !options.includes(item))) return null;
      if (unique.length < step.min || unique.length > step.max) return null;
      return unique;
    }
    case 'platforms': {
      if (!Array.isArray(value)) return null;
      const unique = [...new Set(value)];
      if (unique.some((item) => !(AGENT_FLOW_PLATFORMS as readonly string[]).includes(item))) return null;
      if (unique.length < step.min || unique.length > step.max) return null;
      return unique;
    }
    case 'amount': {
      if (typeof value !== 'number' || !Number.isFinite(value)) return null;
      return value >= step.min && value <= step.max ? Math.round(value * 100) / 100 : null;
    }
    case 'text': {
      if (typeof value !== 'string') return null;
      const text = value.trim();
      return text.length > 0 && text.length <= step.maxLength ? text : null;
    }
    case 'asset': {
      if (typeof value !== 'object' || value === null || Array.isArray(value)) return null;
      if ('attachment' in value) {
        if (!step.sources.includes('upload')) return null;
        return value.attachment < context.attachments ? value : null;
      }
      if ('find' in value) return step.sources.includes('find') ? value : null;
      if ('ref' in value) {
        if (value.ref !== step.role) return null;
        const source: AgentFlowAssetSource = value.ref === 'product' ? 'catalog' : 'library';
        return step.sources.includes(source) ? value : null;
      }
      return null;
    }
  }
}

/** Bir akışın mesajda kaç eki işaret ettiği — arayüz ekleri bu sırayla öne koyuyor. */
export function agentFlowAttachmentIndexes(answers: AgentFlowAnswers): number[] {
  const out: number[] = [];
  for (const value of Object.values(answers)) {
    if (typeof value === 'object' && value !== null && !Array.isArray(value) && 'attachment' in value) {
      out.push(value.attachment);
    }
  }
  return out;
}
